import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Property, Obligation, Reminder, Project, Expense } from './types';
import * as db from './lib/db';

interface AppData {
  properties: Property[];
  obligations: Obligation[];
  remindersByObligation: Record<string, Reminder[]>;
  projects: Project[];
  expenses: Expense[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getProperty: (id: string | null) => Property | undefined;
}

const AppDataContext = createContext<AppData | null>(null);

export function AppDataProvider({ children }: { children: React.ReactNode }) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [remindersByObligation, setRemindersByObligation] = useState<Record<string, Reminder[]>>(
    {}
  );
  const [projects, setProjects] = useState<Project[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [props, o, r, p, e] = await Promise.all([
        db.listProperties(),
        db.listObligations(),
        db.listAllReminders(),
        db.listProjects(),
        db.listExpenses(),
      ]);
      setProperties(props);
      setObligations(o);
      setRemindersByObligation(r);
      setProjects(p);
      setExpenses(e);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getProperty = useCallback(
    (id: string | null) => (id ? properties.find((p) => p.id === id) : undefined),
    [properties]
  );

  const value = useMemo(
    () => ({
      properties,
      obligations,
      remindersByObligation,
      projects,
      expenses,
      loading,
      error,
      refresh,
      getProperty,
    }),
    [
      properties,
      obligations,
      remindersByObligation,
      projects,
      expenses,
      loading,
      error,
      refresh,
      getProperty,
    ]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used inside AppDataProvider');
  return ctx;
}
